import {
  ApiErrorSchema,
  DEFAULT_PLAN_MAX_DOCUMENTS,
  MAX_PLAN_MAX_DOCUMENTS,
  type ApprovedIngestItem,
  type FieldError,
  type IngestPlannerReason,
  type PlanSharepointIngestRequest,
  type PlanSharepointIngestResponse,
} from '@clm/shared-types';

import { ApiError } from '../../api/errors';
import {
  executeSharepointIngest,
  planSharepointIngest,
  reopenSharepointDocument,
  skipSharepointDocument,
} from '../../api/ingest';
import { classifyApiError, isSignInDenial } from '../../api/problem';
import { loginRequest, msalInstance } from '../../auth/msal';
import {
  beginRequest,
  getIngestPlannerState,
  isCurrentRequest,
  resetIngestPlannerStore,
  rowKey,
  setFieldError,
  setPhase,
  setPlan,
  isCorrection,
  rowTags,
  setOverride,
  markSkipped,
  replacedPlanOf,
} from './planner-store';

/**
 * The Ingest planner's calls — plan, execute, Re-plan, Start over — written
 * against the store rather than a component (CLM-67, D-67-6).
 *
 * Each promise here is started by a user action on the surface and finished by
 * the store: its result is written with `setPlan` / `setPhase` after checking
 * the request is still the current one. The surface being unmounted in the
 * meantime changes nothing — that is the whole point. Only a newer request,
 * "Start over" or sign-out (both via `resetIngestPlannerStore`) make a result
 * stale, and a stale result is dropped, never written.
 */

type Kept = { readonly plan: PlanSharepointIngestResponse; readonly request: PlanSharepointIngestRequest };

/** `field_errors` from a well-formed API error body; `[]` for anything else. */
function fieldErrorsOf(error: unknown): readonly FieldError[] {
  if (!(error instanceof ApiError)) return [];
  const parsed = ApiErrorSchema.safeParse(error.body);
  return parsed.success ? parsed.data.field_errors : [];
}

/** One error path for both calls; the wording differs only where the action does. */
function fail(action: 'plan' | 'execute', error: unknown, kept: Kept | null = null): void {
  if (error instanceof ApiError && error.isUnauthenticated) {
    // The gateway rejected the token — re-authenticate the same way the rest
    // of the app does: a redirect to Entra, never a popup.
    setPhase({ kind: 'session_expired' });
    void msalInstance.loginRedirect(loginRequest);
    return;
  }

  if (error instanceof ApiError) {
    const kind = classifyApiError(error);
    // A 400 carries `field_errors` (CLM-67, D-67-5): the sharing-link reason
    // comes back as a `folder_link` entry, which also shows under the input.
    const fieldErrors = kind === 'validation' ? fieldErrorsOf(error) : [];
    const linkError = fieldErrors.find((entry) => entry.path === 'folder_link');
    if (linkError !== undefined) setFieldError(linkError.message);
    setPhase({
      kind: 'error',
      action,
      message: error.message,
      correlation_id: error.correlation_id,
      // A sign-in denial first: gateway step 4 refuses before the route, so a
      // CEQ-CLM-Admin member with no scope group gets a 403 here too, and the
      // role sentence would tell her she needs the role she holds (30 Sep 2026).
      // Otherwise the endpoints answer 404, not 403, to anyone without the
      // Contributor or Admin role (R-94-1 / CLM-94) — to anyone else they do not
      // exist. Treat both as the role deny.
      denial: isSignInDenial(kind) ? kind : error.status === 403 || error.status === 404 ? 'role' : null,
      field_errors: fieldErrors,
      kept,
    });
    return;
  }

  // A ZodError here means the API answered but off-contract — a defect to
  // report, never data to render.
  const offContract = error instanceof Error && error.name === 'ZodError';
  setPhase({
    kind: 'error',
    action,
    message: offContract
      ? action === 'plan'
        ? 'The API answered, but its response did not match the plan-sharepoint-ingest contract this build was compiled against. Nothing was ingested; report this to the API owners.'
        : 'The API answered, but its response did not match the execute-sharepoint-ingest contract this build was compiled against. Nothing further was ingested by this page; check the API audit and report this to the API owners.'
      : error instanceof Error
        ? error.message
        : String(error),
    correlation_id: null,
    denial: null,
    field_errors: [],
    kept,
  });
}

async function runPlan(request: PlanSharepointIngestRequest, controller: AbortController): Promise<void> {
  try {
    const plan = await planSharepointIngest(request, controller.signal);
    if (!isCurrentRequest(controller)) return;
    setPlan(plan, request);
  } catch (error) {
    if (!isCurrentRequest(controller)) return;
    fail('plan', error);
  }
}

/**
 * CLM-67 — post exactly the ticked triples. `max_documents` and
 * `include_unsigned_pdfs` mirror the plan request (D-67-3) and are present
 * only when the plan carried them, so the re-crawl sees the same candidates.
 */
async function runExecute(
  kept: Kept,
  approved: readonly ApprovedIngestItem[],
  controller: AbortController,
): Promise<void> {
  const { plan, request } = kept;
  try {
    const receipt = await executeSharepointIngest(
      {
        folder_link: request.folder_link,
        approved: [...approved],
        ...(request.max_documents === undefined ? {} : { max_documents: request.max_documents }),
        ...(request.include_unsigned_pdfs === undefined
          ? {}
          : { include_unsigned_pdfs: request.include_unsigned_pdfs }),
      },
      controller.signal,
    );
    if (!isCurrentRequest(controller)) return;
    setPhase({ kind: 'receipt', plan, request, approved, receipt });
  } catch (error) {
    if (!isCurrentRequest(controller)) return;
    fail('execute', error, kept);
  }
}

/**
 * Re-plan (A4): the same request again, never a local patch of the plan. The
 * plan being replaced rides on the loading phase (CLM-122, R-122-4), so a tick
 * on a Type pending row survives while the document is unchanged.
 */
export function replan(request: PlanSharepointIngestRequest): void {
  const previous = replacedPlanOf(getIngestPlannerState().phase);
  const controller = beginRequest();
  setFieldError(null);
  setPhase({ kind: 'loading', previous });
  void runPlan(request, controller);
}

/**
 * The form's submit: validate what was typed, build the request and plan.
 * The option is on the wire only when on, so a plan made without it is
 * byte-identical to a CLM-43 plan (D-67-3).
 */
export function planFromForm(): void {
  const { form } = getIngestPlannerState();

  const link = form.folder_link.trim();
  if (!/^https:\/\/\S+$/.test(link)) {
    setFieldError('Paste the full https:// sharing link that SharePoint’s “Copy link” produces.');
    return;
  }

  let cap: number | undefined;
  const rawCap = form.max_documents.trim();
  if (rawCap !== '') {
    const parsed = Number(rawCap);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_PLAN_MAX_DOCUMENTS) {
      setFieldError(
        `The document cap must be a whole number from 1 to ${MAX_PLAN_MAX_DOCUMENTS}, or blank for the server default of ${DEFAULT_PLAN_MAX_DOCUMENTS}.`,
      );
      return;
    }
    cap = parsed;
  }

  replan({
    folder_link: link,
    ...(cap === undefined ? {} : { max_documents: cap }),
    ...(form.include_unsigned ? { include_unsigned_pdfs: true } : {}),
  });
}

/** The plan an execute can be posted from: a plan, or a failed execute that kept its plan. */
function executableSource(): Kept | null {
  const { phase } = getIngestPlannerState();
  if (phase.kind === 'plan') return { plan: phase.plan, request: phase.request };
  if (phase.kind === 'error') return phase.kept;
  return null;
}

/**
 * Execute the ticked rows: plan order, ticked rows only, coordinates only —
 * nothing else can be represented on the wire (the request schema is
 * `.strict()`). A no-op with nothing ticked or nothing to execute from.
 */
export function executeSelected(): void {
  const source = executableSource();
  if (source === null) return;
  const { selected, overrides } = getIngestPlannerState();
  const approved: ApprovedIngestItem[] = source.plan.to_ingest
    .filter((doc) => selected.has(rowKey(doc)))
    .map((doc) => {
      /* CLM-77 — a planned row carries its tags and nothing else (CLM-118: no type override, R-118-18). */
      const tags = rowTags(overrides, rowKey(doc));
      return {
        drive_id: doc.drive_id,
        item_id: doc.item_id,
        etag: doc.etag,
        ...(tags.length === 0 ? {} : { tags: [...tags] }),
      };
    });
  /**
   * CLM-71 — the ticked corrected rows follow, each with its override fields and
   * nothing else. Planned rows above stay triples (plus their tags): a number or
   * party override on a row the planner resolved is never sent (the server would
   * ignore it anyway, D-71-5), and no row carries a type (CLM-118).
   */
  for (const doc of source.plan.to_skip) {
    const override = overrides.get(rowKey(doc));
    /* CLM-77 — a tags-only entry never makes a skipped row executable. */
    if (override === undefined || !isCorrection(override) || !selected.has(rowKey(doc)) || doc.etag === null) continue;
    approved.push({
      drive_id: doc.drive_id,
      item_id: doc.item_id,
      etag: doc.etag,
      ...(override.sow_number_override === undefined ? {} : { sow_number_override: override.sow_number_override }),
      ...(override.party_key_override === undefined ? {} : { party_key_override: override.party_key_override }),
      ...(override.tags === undefined || override.tags.length === 0 ? {} : { tags: [...override.tags] }),
      /* CLM-126 (R-126-12) — only meaningful alongside party_key_override; the
       * server re-checks it equals that key before crediting the suggested
       * actor, so sending it whenever the editor set it is never trusted outright. */
      ...(override.suggestion_accepted === undefined ? {} : { suggestion_accepted: override.suggestion_accepted }),
    });
  }
  if (approved.length === 0) return;
  const controller = beginRequest();
  setPhase({ kind: 'executing', plan: source.plan, request: source.request, approved });
  void runExecute(source, approved, controller);
}

/**
 * "Start over": the empty page again — form, plan, ticks and receipt gone, any
 * request in flight abandoned. The same reset sign-out performs.
 */
export function startOver(): void {
  resetIngestPlannerStore();
}

/* -------------------------------------------------------------------------- */
/* CLM-77 — tags on a row                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Set the tags for one row of the current plan (a planned row, or a corrected
 * skip). Kept on the row's `RowOverride` so a correction and its tags travel
 * together into `approved[]`; a planned row gets a tags-only entry that
 * `isCorrection` says is not a correction — it never ticks, un-ticks or
 * re-plans anything. An empty set removes a tags-only entry and strips the tags
 * from a real correction.
 */
export function setRowTags(key: string, tags: readonly string[]): void {
  const { overrides, phase } = getIngestPlannerState();
  const existing = overrides.get(key);
  const next = [...new Set(tags)];
  if (existing !== undefined) {
    if (next.length === 0 && !isCorrection(existing)) {
      setOverride(key, null);
      return;
    }
    const { tags: _dropped, ...rest } = existing;
    void _dropped;
    setOverride(key, next.length === 0 ? rest : { ...rest, tags: next });
    return;
  }
  if (next.length === 0) return;
  void phase;
  const source = executableSource();
  const doc = source?.plan.to_ingest.find((candidate) => rowKey(candidate) === key);
  if (doc === undefined) return;
  setOverride(key, {
    etag: doc.etag,
    tags: next,
    display: { party_label: null, processing_restricted: doc.processing_restricted, sow_number_shape: null },
  });
}

/* -------------------------------------------------------------------------- */
/* CLM-115 — skip and reopen                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Record a decision not to ingest one document, then move it out of the review
 * list.
 *
 * The store is updated only after the server confirms: an optimistic move would
 * show a row as settled that is not, and this decision is the one thing on this
 * surface that outlives the tab. A 409 means somebody else skipped it first,
 * which is the same outcome the reviewer wanted, so the row moves anyway rather
 * than presenting an error for a state that already matches their intent.
 *
 * CLM-122 (R-122-6): a Type pending row may be skipped too — it passes its
 * `type_pending_reason` and its eTag, recorded verbatim as `planner_reason`.
 */
export async function skipRow(document: {
  readonly drive_id: string;
  readonly item_id: string;
  readonly etag: string | null;
  readonly reason: IngestPlannerReason;
}): Promise<void> {
  const key = rowKey(document);
  try {
    await skipSharepointDocument({
      drive_id: document.drive_id,
      item_id: document.item_id,
      skipped_etag: document.etag,
      planner_reason: document.reason,
    });
  } catch (error) {
    if (error instanceof ApiError && classifyApiError(error) === 'conflict') {
      markSkipped(key);
      return;
    }
    const { phase } = getIngestPlannerState();
    fail('execute', error, phase.kind === 'plan' ? { plan: phase.plan, request: phase.request } : null);
    return;
  }
  markSkipped(key);
}

/**
 * Put a skipped document back in front of the next plan.
 *
 * Re-plans afterwards rather than editing the current one in place: the row has
 * to be re-crawled to know which group it now belongs in, and guessing would
 * put it under a verdict the planner never gave.
 */
export async function reopenRow(document: {
  readonly drive_id: string;
  readonly item_id: string;
}): Promise<void> {
  const { phase } = getIngestPlannerState();
  try {
    await reopenSharepointDocument({ drive_id: document.drive_id, item_id: document.item_id });
  } catch (error) {
    fail('execute', error, phase.kind === 'plan' ? { plan: phase.plan, request: phase.request } : null);
    return;
  }
  if (phase.kind === 'plan') replan(phase.request);
}
